import { useState, type FormEvent, type ReactNode } from 'react';
import { CircleAlert, Trash2 } from 'lucide-react';
import { TAG_COLORS, TAG_NAME_MAX } from '@cp/shared';
import { Button, ConfirmDialog, Input, Popover, TagColorPicker } from '@/components/ui';
import { useDeleteTag, useSaveTag } from '@/hooks/useTags';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import type { TagRecord } from '@/types/api';

/**
 * Creating a tag, or renaming, recolouring and deleting one, in a small popover
 * anchored to it — the way it is done in the tools this team already uses. A
 * full dialog for renaming one label is a heavier interruption than the task
 * deserves.
 */
export function TagEditor({
  open,
  tag,
  onClose,
  onDeleted,
}: {
  readonly open: boolean;
  readonly tag?: TagRecord;
  readonly onClose: () => void;
  readonly onDeleted: () => void;
}): ReactNode {
  const [name, setName] = useState(tag?.name ?? '');
  const [color, setColor] = useState<string>(tag?.color ?? 'blue');
  const [confirming, setConfirming] = useState(false);

  const save = useSaveTag(tag, {
    onSuccess: () => {
      if (!tag) setName('');
      onClose();
    },
  });

  const remove = useDeleteTag({
    onSuccess: (_result, removed) => {
      toast.success(`Тег «${removed.name}» удалён`);
      setConfirming(false);
      onDeleted();
    },
  });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (name.trim().length > 0 && !save.isPending) save.mutate({ name, color });
  };

  return (
    <>
      <Popover open={open && !confirming} onClose={onClose} label={tag ? 'Настройка тега' : 'Новый тег'}>
        <form className="stack stack-tight" onSubmit={submit}>
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Название тега"
            aria-label="Название тега"
            maxLength={TAG_NAME_MAX}
            autoFocus
          />

          <TagColorPicker value={color} colors={TAG_COLORS} onChange={setColor} />

          {save.isError ? (
            <p className="field-error" role="alert">
              <CircleAlert size={13} aria-hidden="true" />
              {errorText(save.error, 'Не удалось сохранить тег')}
            </p>
          ) : null}

          <div className="row-between">
            {tag ? (
              <Button type="button" size="sm" variant="danger" icon={Trash2} onClick={() => setConfirming(true)}>
                Удалить
              </Button>
            ) : (
              <span />
            )}
            <Button type="submit" size="sm" variant="primary" busy={save.isPending} disabled={name.trim().length === 0}>
              {tag ? 'Сохранить' : 'Создать'}
            </Button>
          </div>
        </form>
      </Popover>

      {tag ? (
        <ConfirmDialog
          open={confirming}
          title={`Удалить тег «${tag.name}»?`}
          description={
            tag.siteCount > 0
              ? `Тег снимется с ${tag.siteCount} сайт(ов). Сами сайты останутся на месте.`
              : 'Тег ни на одном сайте не стоит.'
          }
          confirmLabel="Удалить"
          destructive
          busy={remove.isPending}
          error={remove.isError ? errorText(remove.error, 'Не удалось удалить тег') : undefined}
          onConfirm={() => remove.mutate(tag)}
          onClose={() => setConfirming(false)}
        />
      ) : null}
    </>
  );
}
